import React, { FC, useEffect, useState } from 'react';
import { view } from '@forge/bridge';

import { RtField } from '../../../../../src/api/servicedeskApi';
import { useProprietaryFieldsOnRequests } from '../../../../../src/hooks/useMlvFieldsOnRequest';
import { FieldModel, SavingModel } from '../../../../../src/shared_models/interfaces';
import ServiceDeskApi from '../../api/servicedeskApi';
import StorageApi from '../../api/storageApi';
import fieldForgeApi from '../../bridge/fieldForgeApi';
import { FieldType } from '../../common/constants';
import { ViewContext } from '../../interfaces';
import EditRegexCustomField from '../../pages/regex/view';

import { EditMultilevelCustomField } from './EditMultilevelCustomField';
import { PortalContext } from './interfaces';
import { FieldWrapper } from './PortalEditStyled';
import { constructViewModelFromPortalContext, getStringAfterLastChar } from './utils';

const SLASH = '/';

type FieldDescription = {
    configId: string;
    fieldType: FieldType;
    name: string;
    description?: string;
};

export const PortalEdit: FC = () => {
    const [portalContext, setPortalContext] = useState<PortalContext | undefined>(undefined);
    const [fieldsIdsToDisplay, setFieldsIdsToDisplay] = useState<Map<string, FieldDescription>>(new Map());
    const [isFetchCompleted, setIsFetchCompleted] = useState(false);
    const [fieldsModels, setFieldsModels] = useState<FieldModel[]>([]);
    const [fieldsOnRequest, existAtLeastOne] = useProprietaryFieldsOnRequests();

    const fetchCustomFieldConfigurationIds = async (rtFields: RtField[], projectId: string, issueTypeId: string) => {
        const map = new Map<string, FieldDescription>();
        for (const field of rtFields) {
            const configId = await getCustomFieldContextId(field.fieldId, projectId, issueTypeId);
            const fieldType = getStringAfterLastChar(field.jiraSchema.custom, SLASH);
            map.set(field.fieldId, {
                configId,
                name: field.name,
                description: field.description,
                fieldType: fieldType as FieldType,
            });
        }
        return map;
    };
    const fetchData = async () => {
        const portalContext = await view.getContext();
        const serviceDeskId = portalContext.extension.portal.id as string;
        const requestTypeId = portalContext.extension.request.typeId as string;

        try {
            const [requestType, serviceDesk, rtModuleConfig] = await Promise.all([
                ServiceDeskApi.getRequestType(serviceDeskId, requestTypeId),
                ServiceDeskApi.getServiceDesk(serviceDeskId),
                StorageApi.getCreateRtModuleConfig(),
            ]);
            setPortalContext({
                context: portalContext,
                projectId: serviceDesk.projectId,
                issueTypeId: requestType.issueTypeId,
            });

            if (!rtModuleConfig?.moduleId) {
                await StorageApi.setCreateRtModuleConfig({ moduleId: portalContext.localId.split('/')[1] });
            }
            setFieldsIdsToDisplay(
                await fetchCustomFieldConfigurationIds(fieldsOnRequest, serviceDesk.projectId, requestType.issueTypeId)
            );
        } catch (exc: any) {
            console.error('error', exc);
        }

        setIsFetchCompleted(true);
    };

    const getCustomFieldContextId = async (fieldId: string, projectId: string, issueTypeId: string) => {
        const fieldConfiguration = await fieldForgeApi.getFieldConfiguration(fieldId, projectId, issueTypeId);
        return fieldConfiguration.id;
    };

    useEffect(() => {
        if (existAtLeastOne) {
            void fetchData();
        }
        void fetchData();
    }, [existAtLeastOne]);

    const contextProvider = async (fieldId: string, portalContext?: PortalContext): Promise<ViewContext> => {
        return Promise.resolve(
            constructViewModelFromPortalContext(
                portalContext?.context.locale,
                fieldId,
                portalContext?.issueTypeId,
                portalContext?.projectId
            )
        );
    };

    const onSave = async (model: SavingModel | string, isValid: boolean, fieldId: string) => {
        const fieldsModel = fieldsModels.filter(field => field.key !== fieldId);
        const newFieldModels: FieldModel[] = [
            ...fieldsModel,
            {
                key: fieldId,
                value: {
                    model,
                    isValid,
                },
            },
        ].filter(field => field.value.model?.values !== '');
        setFieldsModels(newFieldModels);
        await view.submit({
            fields: [...newFieldModels],
            isValid: newFieldModels.every(field => field.value.isValid),
        });
    };

    return (
        <>
            {isFetchCompleted &&
                Array.from(fieldsIdsToDisplay.keys()).map(key => {
                    const fieldType = fieldsIdsToDisplay.get(key)?.fieldType;

                    return (
                        <FieldWrapper key={key}>
                            {fieldType === FieldType.REGEX ?
                                <EditRegexCustomField
                                    key={key}
                                    fieldName={fieldsIdsToDisplay?.get(key)?.name}
                                    fieldDescription={fieldsIdsToDisplay?.get(key)?.description}
                                    fieldId={key}
                                    onSaveOverride={(model: string, isValid: boolean) => onSave(model, isValid, key)}
                                />
                            :   <EditMultilevelCustomField
                                    key={key}
                                    fieldName={fieldsIdsToDisplay?.get(key)?.name}
                                    fieldDescription={fieldsIdsToDisplay?.get(key)?.description}
                                    customFieldContextId={fieldsIdsToDisplay?.get(key)?.configId}
                                    onSaveOverride={(model: SavingModel, isValid: boolean) =>
                                        onSave(model, isValid, key)
                                    }
                                    contextProvider={() => contextProvider(key, portalContext)}
                                />
                            }
                        </FieldWrapper>
                    );
                })}
        </>
    );
};
